import React from "react";
import { useNavigate } from "react-router-dom";
import medalBadge from "@/assets/medal-badge.png";
import { useUserChallengesQuery } from "@/hooks/useChallengesQuery";
import { useChallenge } from "@/contexts/ChallengeContext";
import { BookOpen } from "lucide-react";

/** Physical right edge — ml-auto is margin on the left side of the box, so the title sits on the visual right regardless of inherited dir. */
function MyActivitySectionTitle() {
  return (
    <h2
      dir="rtl"
      className="w-fit max-w-full ml-auto block text-lg font-semibold text-foreground text-right"
    >
      הפעילות שלי
    </h2>
  );
}

export const ChallengesSection = React.memo(function ChallengesSection() {
  const navigate = useNavigate();
  const { data: challenges = [], isLoading, isError, refetch, isFetching } = useUserChallengesQuery();
  // The list is filtered to the context's current challenge, so a failed
  // context load empties it too — both count as "couldn't load".
  const { activeChallengeLoadFailed, reloadActiveChallenge } = useChallenge();
  const [retrying, setRetrying] = React.useState(false);
  const retry = async () => {
    setRetrying(true);
    try {
      await reloadActiveChallenge();
      await refetch();
    } finally {
      setRetrying(false);
    }
  };

  if (isLoading) {
    return <section className="w-full px-0 py-4 space-y-4" dir="rtl">
      <MyActivitySectionTitle />
      <div className="bg-card rounded-2xl p-5 md:p-6 shadow-sm">
        <div className="flex gap-3 items-start animate-pulse">
          <div className="h-11 w-11 rounded-lg bg-muted shrink-0" />
          <div className="flex-1 space-y-3 pt-1">
            <div className="h-4 bg-muted rounded w-3/4" />
            <div className="h-4 bg-muted rounded" />
          </div>
        </div>
      </div>
    </section>;
  }

  // Only when there's nothing cached: a failed background refresh keeps
  // showing what the member already had.
  if ((isError || activeChallengeLoadFailed) && challenges.length === 0) {
    return <section className="w-full px-0 py-4 space-y-4" dir="rtl">
      <MyActivitySectionTitle />
      <div className="bg-card rounded-2xl p-6 shadow-sm flex flex-col items-center text-center gap-3">
        <div className="space-y-1">
          <p className="font-semibold text-foreground">לא הצלחנו לטעון את הפעילות שלכם</p>
          <p className="text-sm text-muted-foreground">בדקו את החיבור לאינטרנט ונסו שוב.</p>
        </div>
        <button
          onClick={retry}
          disabled={retrying || isFetching}
          className="mt-1 min-h-11 rounded-xl bg-primary px-5 text-sm font-semibold text-primary-foreground active:scale-[0.98] transition-transform disabled:opacity-60"
        >
          {retrying || isFetching ? "טוענים…" : "לנסות שוב"}
        </button>
      </div>
    </section>;
  }

  if (challenges.length === 0) {
    return <section className="w-full px-0 py-4 space-y-4" dir="rtl">
      <MyActivitySectionTitle />
      <div className="bg-card rounded-2xl p-6 shadow-sm flex flex-col items-center text-center gap-3">
        <div className="h-12 w-12 rounded-full bg-primary/10 grid place-items-center">
          <BookOpen className="h-6 w-6 text-primary" />
        </div>
        <div className="space-y-1">
          <p className="font-semibold text-foreground">עוד לא הצטרפת לפעילות</p>
          <p className="text-sm text-muted-foreground">מצאו אתגר או קורס שמתאים לכם והתחילו היום</p>
        </div>
        <button
          onClick={() => navigate('/library')}
          className="mt-1 rounded-xl bg-primary px-4 py-2 text-sm font-semibold text-primary-foreground active:scale-[0.98] active:bg-primary/90 transition-all focus-visible:outline focus-visible:outline-2 focus-visible:outline-primary focus-visible:outline-offset-2"
        >
          לספרייה
        </button>
      </div>
    </section>;
  }

  return <section className="w-full px-0 py-4 space-y-4" dir="rtl">
      <MyActivitySectionTitle />

      <div className="space-y-3">
        {challenges.map((challenge) => {
          const isCourse = challenge.challengeType === 'course';
          const progressColor = isCourse ? 'bg-accent-purple' : 'bg-primary';
          // Always use the dynamically-calculated day from useChallengesQuery
          // (recalculated on every query run via calculateCurrentDay).
          // Never use progress?.currentDay — that's a stale DB snapshot
          // written at enrollment time and not updated when time passes.
          const displayCurrentDay = challenge.currentDay;
          const displayProgress = challenge.isCourseCompleted
            ? 100
            : Math.round((Math.max(0, displayCurrentDay) / challenge.totalDays) * 100);

          return (
            <button
              key={challenge.id}
              className="w-full bg-card rounded-2xl p-5 md:p-6 shadow-sm text-right active:scale-[0.98] transition-transform"
              dir="rtl"
              onClick={() => navigate(`/challenge/${challenge.id}/day/${displayCurrentDay}`)}
            >
              <div className="flex gap-3 items-start">
                {/* Icon: medal for challenges, book for courses */}
                <div className="flex justify-center items-center flex-shrink-0 self-stretch">
                  {isCourse ? (
                    <div className="h-11 w-11 flex items-center justify-center rounded-lg bg-accent-purple/10">
                      <BookOpen className="h-6 w-6 text-accent-purple" />
                    </div>
                  ) : (
                    <img
                      src={medalBadge}
                      alt=""
                      className="h-11 w-auto object-contain"
                    />
                  )}
                </div>

                {/* Content: 2 rows */}
                <div className="flex-1 flex flex-col gap-1">
                  {/* Row 1: Title */}
                  <div className="text-right flex items-center gap-2">
                    <span className="text-base font-semibold text-foreground">{challenge.title}</span>
                  </div>

                  {/* Row 2: Progress bar */}
                  <div className="relative h-4">
                    <div className="w-full h-full bg-muted rounded-sm" />
                    <div
                      className={`absolute top-0 right-0 h-full ${progressColor} rounded-sm transition-[width] duration-500`}
                      style={{ width: `${displayProgress}%` }}
                    />
                    <div className="absolute inset-0 flex items-center justify-center">
                      <span className="text-xs text-foreground" dir="ltr">
                        {challenge.isCourseCompleted
                          ? 'הושלם ✓'
                          : displayCurrentDay < 1
                            ? 'יום הכנה'
                            : `${displayCurrentDay}/${challenge.totalDays}`
                        }
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </button>
          );
        })}
      </div>
    </section>;
});
